import React, {Suspense} from 'react'
import {Box, Text} from '@chakra-ui/react'
import PropTypes from 'prop-types'

/**
 * Error boundary component that catches rendering errors in module components
 * and displays an error message in a red box with module configuration details
 */
class ModuleErrorBoundary extends React.Component {
    constructor(props) {
        super(props)
        this.state = {hasError: false, error: null, hydrationComplete: false}
    }

    static getDerivedStateFromError(error) {
        return {hasError: true, error}
    }

    componentDidCatch(error, errorInfo) {
        console.error('Module error:', error, errorInfo)
    }

    componentDidMount() {
        // Mark hydration as complete - allows us to remove Suspense boundary
        // after hydration finishes to prevent state update conflicts
        this.setState({hydrationComplete: true})
    }

    render() {
        const {moduleType, assetId, position, moduleConfig} = this.props
        if (this.state.hasError) {
            return (
                <Box border="1px solid red" p={4} m={4}>
                    <Text color="red">
                        <span>Module rendering error for&nbsp;</span>
                        <strong>{moduleType}</strong>
                        <span>&nbsp;from content asset&nbsp;</span>
                        <strong>{assetId}</strong>
                        <span>&nbsp;position&nbsp;</span>
                        <strong>{position}</strong>
                    </Text>
                    <Text color="red" mt={2}>
                        <span>Error:&nbsp;</span>
                        <strong>{this.state.error?.message || 'An unknown error occurred'}</strong>
                    </Text>
                    <Text as="pre" bg="red" mt={2}>
                        {JSON.stringify(moduleConfig, null, 2)}
                    </Text>
                    <Text as="pre" bg="red" mt={2}>
                        {this.state.error?.stack || 'No stack trace available'}
                    </Text>
                </Box>
            )
        }

        // SSR error boundary as per https://github.com/reactjs/rfcs/blob/main/text/0215-server-errors-in-react-18.md#basic-example
        // Only use Suspense during SSR and hydration, remove it after hydration completes
        // to prevent state updates during hydration from causing Suspense boundary errors
        if (!this.state.hydrationComplete) {
            return (
                <Suspense
                    fallback={
                        <Box border="1px solid red" p={4} m={4}>
                            <Text color="red">
                                <span>Module rendering error for&nbsp;</span>
                                <strong>{moduleType}</strong>
                                <span>&nbsp;from content asset&nbsp;</span>
                                <strong>{assetId}</strong>
                                <span>&nbsp;position&nbsp;</span>
                                <strong>{position}</strong>
                            </Text>
                            <Text as="pre" bg="red" mt={2}>
                                {JSON.stringify(moduleConfig, null, 2)}
                            </Text>
                        </Box>
                    }
                >
                    {this.props.children}
                </Suspense>
            )
        }

        return this.props.children
    }
}

ModuleErrorBoundary.propTypes = {
    children: PropTypes.node,
    moduleType: PropTypes.string,
    assetId: PropTypes.string,
    position: PropTypes.number,
    moduleConfig: PropTypes.object
}

export default ModuleErrorBoundary
